import {
  ArrowRightFill,
  CubeOutline,
  DifferentiatorOutline,
  ExcalamationInSquareOutline,
  FileTextOutline,
  FilledStack,
  FlashlightOutline,
  IntegrationOutline,
  JourneyWithDotsOutline,
  TargetOutline,
  TrophyWithKey,
  TwoFaces,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Avatar, InfoPill, Stack, Typography } from '@lyearn/ui';
import { TypographyWithBulletPoint } from '@/components/TypographyWithBulletPoint';
import useNavigation from '@/hooks/useNavigation';
import { avatars } from '@/modules/session/utils/getRandomAvatar';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';

import EmptyState from '../../../KnowledgeAssetDetails/components/EmptyState';
import { useKnowledgeAssetsQuery } from '../../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import { SolutionTab } from '..';

interface KnowledgeAssetType {
  id: string;
  name: string;
  cover?: {
    url?: string;
  };
  childKnowledgeAssets?: Array<{
    id: string;
    name: string;
    cover?: {
      url?: string;
    };
  }>;
}

interface OverviewPageProps {
  solutionName: string;
  description: string;
  highlights: string[];
  painPoints: string[];
  trendsAndPOV: string[];
  targetPersonas: {
    practitioners: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
    executiveBuyers: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
    decisionMakers: Array<{
      node?: {
        id?: string;
        name?: string;
        cover?: {
          url?: string;
        };
      };
    }>;
  };
  differentiators: string[];
  successKeyMetrics: string[];
  competitors: string[];
  platformCapabilities: string[];
  productCapabilities: string[];
  typicalIntegrations: string[];
  personaJourney?: string;
  setCurrentTab?: React.Dispatch<React.SetStateAction<SolutionTab>>;
}

const OverviewPage = ({
  solutionName,
  description,
  highlights,
  painPoints,
  trendsAndPOV,
  targetPersonas,
  differentiators,
  successKeyMetrics,
  competitors,
  platformCapabilities,
  productCapabilities,
  typicalIntegrations,
  personaJourney,
  setCurrentTab,
}: OverviewPageProps) => {
  const isAdminRoute = useAdminRoute();
  const navigation = useNavigation();
  const journeyIds =
    personaJourney
      ?.split('|')
      .filter(Boolean)
      .flatMap((id) => id.split(',').map((i) => i.trim()))
      .filter(Boolean) || [];

  const [{ data: journeyData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: journeyIds,
      },
    },
    pause: !journeyIds.length,
  });

  const journeyPersonas = (journeyData?.contents?.edges || []) as Array<{
    node: KnowledgeAssetType;
  }>;

  const cards = [
    {
      title: 'Description',
      icon: FileTextOutline,
      responses: [description],
      showBullets: false,
    },
    {
      title: 'Highlights',
      icon: FlashlightOutline,
      responses: highlights,
      showBullets: true,
    },
    {
      title: 'Pain Points',
      icon: ExcalamationInSquareOutline,
      responses: painPoints,
      showBullets: true,
    },
  ];

  const valuePositioningCards = [
    {
      title: 'Key Differentiators',
      icon: DifferentiatorOutline,
      responses: differentiators,
    },
    {
      title: 'Success Key Metrics',
      icon: TrophyWithKey,
      responses: successKeyMetrics,
    },
    {
      title: 'Competitors',
      icon: TwoFaces,
      responses: competitors,
    },
  ];

  const cxArchitectureCards = [
    {
      title: 'Platform capabilities',
      icon: FilledStack,
      responses: platformCapabilities,
    },
    {
      title: 'Product capabilities',
      icon: CubeOutline,
      responses: productCapabilities,
    },
    {
      title: 'Typical integrations',
      icon: IntegrationOutline,
      responses: typicalIntegrations,
    },
  ];

  const renderCardContent = (responses: string[], showBullets?: boolean) => {
    if (!responses || responses.length === 0 || (responses.length === 1 && !responses[0])) {
      return <EmptyState />;
    }

    return responses.map((response: string, responseIndex: number) => (
      <Stack key={responseIndex} direction="col" gapY={4}>
        {showBullets ? (
          <TypographyWithBulletPoint
            className="text-text-secondary"
            text={response}
            variant="body-long-01"
          />
        ) : (
          <Typography className="text-text-secondary" variant="body-long-01">
            {response}
          </Typography>
        )}
      </Stack>
    ));
  };

  const renderValuePositioningCard = (card: { title: string; responses: string[] }) => {
    if (!card.responses || card.responses.length === 0) {
      return <EmptyState />;
    }

    return card.responses.map((response: string, responseIndex: number) => {
      const [title = '', content = ''] = response.split('##').map((s) => s.trim());
      const displayContent = content || response;
      const displayTitle = content ? title : '';

      return (
        <Stack
          key={responseIndex}
          className={cn(
            'py-12',
            responseIndex !== card.responses.length - 1 && 'border-b border-border',
          )}
          direction="col"
          gapY={4}>
          {displayTitle && <Typography variant="label-01">{displayTitle}</Typography>}
          <Typography className="text-text-secondary" variant="body-long-01">
            {displayContent}
          </Typography>
        </Stack>
      );
    });
  };
  const onClickPersonasCard = (id?: string) => {
    if (!id) return;
    let path = routes.KnowledgeAssetLearnerDetails(id, 'persona');
    if (isAdminRoute) {
      path = routes.KnowledgeAssetDetails(id, 'persona');
    }
    navigation.push(path);
  };
  return (
    <Stack
      className="max-w-[101.2rem] mx-64 mt-48 mb-128 xs:min-w-full xs:p-12 xs:mt-12"
      direction="col"
      gapY={21}>
      <Typography variant="display-xl">{solutionName}</Typography>

      {/* Solution positioning section */}
      <Stack className="mt-[11px]" direction="col" gapY={21}>
        <Typography variant="display-m">Solution positioning</Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
          {cards.map((card, index) => (
            <Stack
              key={index}
              className="border border-border rounded-12 p-20"
              direction="col"
              gapY={12}>
              <Stack alignItems="center" gapX={8}>
                <card.icon fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">{card.title}</Typography>
              </Stack>
              <Stack direction="col" gapY={8}>
                {renderCardContent(card.responses, card.showBullets)}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Target Persona section */}
      <Stack className="border border-border rounded-12 p-20" direction="col" gapY={16}>
        <Stack alignItems="center" gapX={8}>
          <TargetOutline fill="transparent" height={23} width={23} />
          <Typography variant="heading-xs">Target Persona</Typography>
        </Stack>
        {targetPersonas.practitioners.length === 0 &&
        targetPersonas.decisionMakers.length === 0 &&
        targetPersonas.executiveBuyers.length === 0 ? (
          <EmptyState />
        ) : (
          <Stack className="grid grid-cols-3 gap-12 xs:grid-cols-1">
            {/* Practitioners Column */}
            <Stack className="w-full" direction="col">
              <Stack className="mb-8">
                <Typography variant="body-short-02"> Practitioner </Typography>
              </Stack>
              <Stack className="border border-border pr-8 rounded-8" direction="col">
                {targetPersonas.practitioners.map((persona, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    className={cn(
                      'p-16 cursor-pointer',
                      index !== targetPersonas.practitioners.length - 1 && 'border-b border-border',
                    )}
                    direction="row"
                    gapX={12}
                    onClick={() => onClickPersonasCard(persona.node?.id)}>
                    <Avatar
                      size="large"
                      src={persona?.node?.cover?.url || avatars[index % 100]?.url}
                    />
                    <Stack direction="col">
                      <Typography variant="label-02">{persona?.node?.name}</Typography>
                      <Typography className="text-text-secondary" variant="body-short-03">
                        Practitioner
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            {/* Decision Makers Column */}

            <Stack className="w-full" direction="col">
              <Stack className="mb-8">
                <Typography variant="body-short-02"> Decision Maker </Typography>
              </Stack>

              <Stack className="border border-border px-8 rounded-8" direction="col">
                {targetPersonas.decisionMakers.map((persona, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    className={cn(
                      'p-16 cursor-pointer',
                      index !== targetPersonas.decisionMakers.length - 1 &&
                        'border-b border-border',
                    )}
                    direction="row"
                    gapX={12}
                    onClick={() => onClickPersonasCard(persona.node?.id)}>
                    <Avatar
                      size="large"
                      src={persona?.node?.cover?.url || avatars[(index + 2) % 100]?.url}
                    />
                    <Stack direction="col">
                      <Typography variant="label-02">{persona?.node?.name}</Typography>
                      <Typography className="text-text-secondary" variant="body-short-03">
                        Decision Maker
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            {/* Executive Buyers Column */}
            <Stack className="w-full" direction="col">
              <Stack className="mb-8">
                <Typography variant="body-short-02"> Executive Buyer </Typography>
              </Stack>
              <Stack className="pl-8 border border-border rounded-8" direction="col">
                {targetPersonas.executiveBuyers.map((persona, index) => (
                  <Stack
                    key={index}
                    alignItems="center"
                    className={cn(
                      'p-16 cursor-pointer',
                      index !== targetPersonas.executiveBuyers.length - 1 &&
                        'border-b border-border',
                    )}
                    direction="row"
                    gapX={12}
                    onClick={() => onClickPersonasCard(persona.node?.id)}>
                    <Avatar
                      size="large"
                      src={persona?.node?.cover?.url || avatars[(index + 1) % 100]?.url}
                    />
                    <Stack direction="col">
                      <Typography variant="label-02">{persona?.node?.name}</Typography>
                      <Typography className="text-text-secondary" variant="body-short-03">
                        Executive Buyer
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>

      {/* Solution Journey section */}
      {isAdminRoute && journeyPersonas.length > 0 && (
        <Stack
          className="w-1/2 border border-border rounded-12 p-20 pb-4"
          direction="col"
          gapY={16}>
          <Stack alignItems="center" gapX={8} justifyContent="between">
            <Stack alignItems="center" gapX={8}>
              <JourneyWithDotsOutline fillColorToken="icon-active" height={23} width={23} />
              <Typography variant="heading-xs">Solution Journey</Typography>
            </Stack>
            <Stack
              alignItems="center"
              className="cursor-pointer"
              direction="row"
              gapX={8}
              onClick={() => setCurrentTab?.(SolutionTab.PERSONA_JOURNEY)}>
              <Typography className="text-text-link hover:underline" variant="body-short-02">
                See more
              </Typography>
              <ArrowRightFill fillColorToken="icon-link" height={16} width={16} />
            </Stack>
          </Stack>
          <Stack direction="col">
            {journeyPersonas.map((journey, index) => {
              const childPersona = journey?.node?.childKnowledgeAssets?.[0];
              return (
                <Stack
                  key={index}
                  alignItems="center"
                  className={cn(
                    'py-20',
                    index !== journeyPersonas.length - 1 && 'border-b border-border',
                  )}
                  direction="row"
                  justifyContent="between">
                  <Typography variant="heading-xs">{journey?.node?.name}</Typography>
                  {childPersona && (
                    <InfoPill
                      appearance="secondary"
                      className="py-4 px-10"
                      label={childPersona.name}
                      size="large"
                      src={childPersona.cover?.url || avatars[index % 100]?.url}
                      type="avatar"
                    />
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}

      {/* CX Architecture section */}
      <Stack className="mt-[11px]" direction="col" gapY={21}>
        <Typography variant="display-m">CX Architecture</Typography>
        <Stack className="grid grid-cols-3 gap-16 xs:grid-cols-1">
          {cxArchitectureCards.map((card, index) => (
            <Stack
              key={index}
              className="border border-border rounded-12 p-20"
              direction="col"
              gapY={12}>
              <Stack alignItems="center" gapX={8}>
                <card.icon fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">{card.title}</Typography>
              </Stack>
              <Stack direction="col" gapY={8}>
                {card.responses.length > 0 ? (
                  card.responses.map((response: string, responseIndex: number) => {
                    const [title = '', content = ''] = response.split('##').map((s) => s.trim());
                    const displayContent =
                      content || (title.endsWith('##') ? title.slice(0, -2) : title);
                    const displayTitle = content ? title : '';

                    return (
                      <Stack
                        key={responseIndex}
                        className={cn(
                          'py-12',
                          responseIndex !== card.responses.length - 1 && 'border-b border-border',
                        )}
                        direction="col"
                        gapY={4}>
                        {displayTitle && <Typography variant="label-01">{displayTitle}</Typography>}
                        <Typography className="text-text-secondary" variant="body-long-01">
                          {displayContent}
                        </Typography>
                      </Stack>
                    );
                  })
                ) : (
                  <EmptyState />
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Value Positioning section */}
      <Stack className="mt-[11px]" direction="col" gapY={21}>
        <Typography variant="display-m">Value Positioning</Typography>
        <Stack className="grid grid-cols-3 gap-16 xs:grid-cols-1">
          {valuePositioningCards.map((card, index) => (
            <Stack
              key={index}
              className="border border-border rounded-12 p-20"
              direction="col"
              gapY={12}>
              <Stack alignItems="center" gapX={8}>
                <card.icon fillColorToken="icon-active" height={24} width={24} />
                <Typography variant="heading-xs">{card.title}</Typography>
              </Stack>
              <Stack direction="col" gapY={8}>
                {card.responses.length > 0 ? renderValuePositioningCard(card) : <EmptyState />}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default OverviewPage;
